import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { AdminApiService } from '../admin-api.service';
import { Role, SystemApp, Permission } from '../models';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

const GLOBAL_KEY = '__global__';

interface SystemRoleCard {
  // Either a real system or the synthetic "Global" bucket
  isGlobal: boolean;
  system?: SystemApp;
  displayName: string;
  key: string;            // sys.key OR GLOBAL_KEY
  total: number;
  superAdmins: number;
  normalRoles: number;
  totalPermissions: number;
  topRoleNames: string[]; // up to 4
  icon: string;
  themeClass: string;
}

@Component({
  standalone: true,
  selector: 'app-roles-page',
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    DialogModule,
    DropdownModule,
    MultiSelectModule,
    InputTextModule,
    TooltipModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  styleUrls: ['./roles.page.scss'],
  templateUrl: './roles.page.html',
})
export class RolesPage implements OnInit {
  api = inject(AdminApiService);
  ms = inject(MessageService);
  confirmService = inject(ConfirmationService);
  router = inject(Router);

  systems: SystemApp[] = [];
  roles: Role[] = [];
  allPerms: Permission[] = [];

  // Picker / detail state
  systemCards: SystemRoleCard[] = [];
  selectedCard: SystemRoleCard | null = null;
  detailRoles: Role[] = [];
  search = '';

  loading = false;
  show = false;
  editId: string | null = null;
  form: Role = { name: '', systemKey: '', description: '', actions: [] };

  permOptions: { label: string; value: string }[] = [];

  // system key → icon + theme (mirrors permissions page)
  private static readonly SYSTEM_THEME: Record<string, { icon: string; themeClass: string }> = {
    lab: { icon: 'pi pi-microchip', themeClass: 'theme-lab' },
    curalab: { icon: 'pi pi-microchip', themeClass: 'theme-lab' },
    hr: { icon: 'pi pi-briefcase', themeClass: 'theme-hr' },
    insurance: { icon: 'pi pi-shield', themeClass: 'theme-insurance' },
    auth: { icon: 'pi pi-key', themeClass: 'theme-auth' },
    system: { icon: 'pi pi-server', themeClass: 'theme-system' },
  };

  ngOnInit() {
    void this.loadAll();
  }

  async loadAll() {
    this.loading = true;
    try {
      const [systems, roles, allPerms] = await Promise.all([
        firstValueFrom(this.api.listSystems()),
        firstValueFrom(this.api.listRoles()),
        this.api.listAllPermissions(),
      ]);
      this.systems = (systems ?? []).filter((s) => s.isActive !== false);
      this.roles = roles ?? [];
      this.allPerms = allPerms ?? [];
      this.permOptions = this.allPerms.map((pp) => ({
        label: this.getPermKey(pp),
        value: this.getPermKey(pp),
      }));
      this.buildSystemCards();
      // If we were already inside a detail view, refresh it
      if (this.selectedCard) {
        this.refreshSelectedCard();
      }
    } catch (err) {
      console.error('Error loading roles page data:', err);
      this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'فشل تحميل البيانات' });
    } finally {
      this.loading = false;
    }
  }

  private buildSystemCards() {
    const cards: SystemRoleCard[] = [];
    // One card per real system
    for (const sys of this.systems) {
      const sysRoles = this.roles.filter((r) => r.systemKey === sys.key);
      cards.push(this.toCard(sys.key, sys.displayName, sysRoles, sys));
    }
    // Synthetic "Global" card for roles with no systemKey
    const globals = this.roles.filter((r) => !r.systemKey);
    if (globals.length > 0) {
      cards.push(this.toCard(GLOBAL_KEY, 'أدوار عامة (Global)', globals, undefined));
    }
    // Show systems with roles first, but keep empty systems visible at the end for adding new roles
    this.systemCards = cards.sort((a, b) => {
      if ((a.total > 0) !== (b.total > 0)) return a.total > 0 ? -1 : 1;
      return b.total - a.total;
    });
  }

  private toCard(
    key: string,
    displayName: string,
    roles: Role[],
    system?: SystemApp,
  ): SystemRoleCard {
    const superAdmins = roles.filter((r) => r.isSystemSuperAdmin).length;
    const totalPerms = roles.reduce((acc, r) => acc + (r.actions?.length ?? 0), 0);
    const topRoleNames = [...roles]
      .sort((a, b) => (b.actions?.length ?? 0) - (a.actions?.length ?? 0))
      .slice(0, 4)
      .map((r) => r.name);

    const isGlobal = key === GLOBAL_KEY;
    const theme = isGlobal
      ? { icon: 'pi pi-globe', themeClass: 'theme-global' }
      : RolesPage.SYSTEM_THEME[key.toLowerCase()] ?? { icon: 'pi pi-th-large', themeClass: 'theme-default' };

    return {
      isGlobal,
      system,
      displayName,
      key,
      total: roles.length,
      superAdmins,
      normalRoles: roles.length - superAdmins,
      totalPermissions: totalPerms,
      topRoleNames,
      icon: theme.icon,
      themeClass: theme.themeClass,
    };
  }

  // ============== Detail view ==============
  selectCard(card: SystemRoleCard) {
    this.selectedCard = card;
    this.search = '';
    this.refreshSelectedCard();
  }

  backToPicker() {
    this.selectedCard = null;
    this.detailRoles = [];
    this.search = '';
  }

  refreshSelectedCard() {
    if (!this.selectedCard) return;
    const key = this.selectedCard.key;
    const q = (this.search ?? '').trim().toLowerCase();
    this.detailRoles = this.roles
      .filter((r) => (this.selectedCard!.isGlobal ? !r.systemKey : r.systemKey === key))
      .filter((r) => {
        if (!q) return true;
        return (
          r.name?.toLowerCase().includes(q) ||
          (r.description ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        // SuperAdmin first, then alphabetical
        if (!!a.isSystemSuperAdmin !== !!b.isSystemSuperAdmin) {
          return a.isSystemSuperAdmin ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
  }

  // ============== CRUD ==============
  openNew() {
    this.editId = null;
    this.form = {
      name: '',
      systemKey: this.selectedCard && !this.selectedCard.isGlobal ? this.selectedCard.key : '',
      description: '',
      actions: [],
    };
    this.show = true;
  }

  openEdit(r: Role) {
    this.editId = r.id || null;

    const validActionKeys = new Set(this.allPerms.map((p) => this.getPermKey(p)));
    const validActions = (r.actions || []).filter((a) => validActionKeys.has(a));
    const deletedActions = (r.actions || []).filter((a) => !validActionKeys.has(a));

    if (deletedActions.length > 0) {
      this.ms.add({
        severity: 'warn',
        summary: 'تحذير',
        detail: `تمت إزالة ${deletedActions.length} صلاحية محذوفة من النظام. سيتم إزالتها عند الحفظ.`,
        life: 5000,
      });
    }

    this.form = {
      name: r.name,
      systemKey: r.systemKey,
      description: r.description,
      actions: validActions,
    };
    this.show = true;
  }

  save() {
    if (!this.form.name?.trim()) {
      this.ms.add({ severity: 'warn', summary: 'تحذير', detail: 'اسم الدور مطلوب' });
      return;
    }

    const validActionKeys = new Set(this.allPerms.map((p) => this.getPermKey(p)));
    this.form.actions = (this.form.actions || []).filter((a) => validActionKeys.has(a));

    const req = this.editId ? this.api.updateRole(this.editId, this.form) : this.api.createRole(this.form);

    req.subscribe({
      next: (result) => {
        const roleId = this.editId || (result as Role).id;
        const actions = this.form.actions ?? [];
        this.api.setRoleActions(roleId!, actions).subscribe({
          next: () => {
            this.ms.add({ severity: 'success', summary: 'تم', detail: 'تم الحفظ' });
            this.show = false;
            void this.loadAll();
          },
          error: () => {
            this.ms.add({
              severity: 'error',
              summary: 'خطأ',
              detail: 'تم حفظ الدور لكن فشل تعيين الصلاحيات',
            });
            this.show = false;
            void this.loadAll();
          },
        });
      },
      error: (err) => {
        const errorMsg = err?.error?.message || 'تعذر الحفظ';
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: errorMsg });
      },
    });
  }

  remove(r: Role) {
    if (!r.id) return;
    if (r.isSystemSuperAdmin) {
      this.ms.add({
        severity: 'warn',
        summary: 'محمي',
        detail: 'أدوار SuperAdmin تُدار من صفحة System Owner، لا من هنا.',
      });
      return;
    }
    this.confirmService.confirm({
      message: `هل أنت متأكد من حذف الدور "${r.name}"؟`,
      header: 'تأكيد الحذف',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'نعم',
      rejectLabel: 'لا',
      accept: () => {
        this.api.deleteRole(r.id!).subscribe({
          next: () => {
            this.ms.add({ severity: 'success', summary: 'تم', detail: 'تم الحذف' });
            void this.loadAll();
          },
          error: (err) => {
            const errorMsg = err?.error?.message || 'تعذر الحذف';
            this.ms.add({ severity: 'error', summary: 'خطأ', detail: errorMsg });
          },
        });
      },
    });
  }

  getPermKey(p: Permission) {
    return `${p.systemKey}:${p.code}`;
  }

  managePermissions(r: Role) {
    if (!r.id) return;
    this.router.navigate(['/roles', r.id, 'permissions']);
  }

  trackCard = (_: number, c: SystemRoleCard) => c.key;
  trackRole = (_: number, r: Role) => r.id ?? r.name;
}
